import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };

function euro(value) {
  return Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function keyOf(item) {
  return `${item.empfaenger}|||${item.kategorie}`;
}

function Pill({ tone = 'slate', children }) {
  const colors = {
    green: ['#dff3e9', '#064734', '#a7d9c2'],
    amber: ['#fff7d6', '#8a5a00', '#f4d36b'],
    red: ['#fff1ef', '#9f1d12', '#ffc9c2'],
    blue: ['#e8f0ff', '#23417a', '#bfd0ff'],
    slate: ['#eef4f1', '#334155', '#d8e4dd'],
  };
  const [bg, color, border] = colors[tone] || colors.slate;
  return <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: '999px', padding: '4px 9px', fontSize: '11px', fontWeight: 900 }}>{children}</span>;
}

export default function GeldfresserPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [applications, setApplications] = useState([]);
  const [months, setMonths] = useState(3);
  const [priority, setPriority] = useState('all');
  const [bulkAction, setBulkAction] = useState('geldfresser');
  const [selected, setSelected] = useState({});
  const [busyKey, setBusyKey] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { load(); }, [months]);

  async function load() {
    setError('');
    try {
      const [r, a] = await Promise.all([
        api.get('/geldfresser/vorschlaege', { params: { monate: months } }),
        api.get('/geldfresser/flag-applications')
      ]);
      setData(r.data);
      setApplications(a.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Vorschlaege konnten nicht geladen werden');
    }
  }

  const suggestions = data?.suggestions || [];
  const filtered = useMemo(() => suggestions.filter(item => {
    if (priority === 'high') return item.score >= 70;
    if (priority === 'open') return !item.ist_geldfresser && !item.ist_ueberfluessig && !item.muss_kuendigen;
    if (priority === 'marked') return item.ist_geldfresser || item.ist_ueberfluessig || item.muss_kuendigen;
    return true;
  }), [suggestions, priority]);

  const selectedItems = useMemo(() => suggestions.filter(item => selected[keyOf(item)]), [suggestions, selected]);

  async function save(item, patch) {
    const key = keyOf(item);
    setBusyKey(key);
    setError('');
    setMessage('');
    try {
      const payload = {
        empfaenger: item.empfaenger,
        kategorie: item.kategorie,
        ist_geldfresser: item.ist_geldfresser,
        ist_ueberfluessig: item.ist_ueberfluessig,
        muss_kuendigen: item.muss_kuendigen,
        ist_notwendig: item.ist_notwendig || 0,
        user_notiz: item.user_notiz || '',
        ...patch
      };
      // Mutual exclusion: last click wins — Geldfresser bleibt unabhängig
      if (patch.muss_kuendigen)    payload.ist_notwendig = 0;
      if (patch.ist_ueberfluessig) payload.ist_notwendig = 0;
      if (patch.ist_notwendig)     { payload.muss_kuendigen = 0; payload.ist_ueberfluessig = 0; }
      const r = await api.post('/geldfresser/empfaenger/flags', payload);
      const undoText = r.data.applicationId ? ` Undo-ID ${r.data.applicationId}.` : '';
      setMessage(`${item.empfaenger}: ${r.data.updated} Buchungen aktualisiert.${undoText}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Bewertung konnte nicht gespeichert werden');
    } finally {
      setBusyKey('');
    }
  }

  function toggleSelected(item, checked) {
    const key = keyOf(item);
    setSelected(prev => ({ ...prev, [key]: checked }));
  }

  function selectTop() {
    const next = {};
    suggestions.filter(item => item.score >= 70 && !item.muss_kuendigen && !item.ist_geldfresser).slice(0, 10).forEach(item => {
      next[keyOf(item)] = true;
    });
    setSelected(next);
  }

  async function saveSelected() {
    if (!selectedItems.length) return;
    setBulkBusy(true);
    setError('');
    setMessage('');
    try {
      const items = selectedItems.map(item => ({
        empfaenger: item.empfaenger,
        kategorie: item.kategorie,
        ist_geldfresser: bulkAction === 'geldfresser' || item.ist_geldfresser,
        ist_ueberfluessig: bulkAction === 'ueberfluessig' || item.ist_ueberfluessig,
        muss_kuendigen: bulkAction === 'kuendigen' || item.muss_kuendigen,
        user_notiz: item.user_notiz || ''
      }));
      const r = await api.post('/geldfresser/empfaenger/bulk-flags', { items });
      const undoText = r.data.applicationId ? ` Undo-ID ${r.data.applicationId}.` : '';
      setMessage(`${r.data.items} Empfaenger gespeichert, ${r.data.updated} Buchungen aktualisiert.${undoText}`);
      setSelected({});
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Sammelbewertung konnte nicht gespeichert werden');
    } finally {
      setBulkBusy(false);
    }
  }

  async function undoApplication(appRow) {
    if (!window.confirm(`Bewertung ${appRow.id} wirklich rückgängig machen?`)) return;
    setBusyKey(`undo-${appRow.id}`);
    setError('');
    setMessage('');
    try {
      const r = await api.post(`/geldfresser/flag-applications/${appRow.id}/undo`);
      setMessage(`${r.data.restored} Buchungen wiederhergestellt.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Rückgängig machen fehlgeschlagen');
    } finally {
      setBusyKey('');
    }
  }

  const totals = useMemo(() => ({
    count: suggestions.length,
    shown: filtered.length,
    selected: selectedItems.length,
    selectedVolume: selectedItems.reduce((sum, item) => sum + Number(item.gesamt || 0), 0),
    yearly: suggestions.reduce((sum, item) => sum + Math.min(Number(item.gesamt || 0), Number(item.durchschnitt || 0) * 12), 0),
    flagged: suggestions.filter(item => item.ist_geldfresser || item.ist_ueberfluessig || item.muss_kuendigen).length
  }), [suggestions, filtered, selectedItems]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <h1 style={{ margin: 0, color: '#064734', fontSize: '25px' }}>Geldfresser prüfen</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>Vorschläge schnell sichten, Kandidaten auswählen und gesammelt bestätigen.</p>
        </div>
        <select value={months} onChange={e => setMonths(Number(e.target.value))} style={input}>
          <option value={1}>1 Monat</option>
          <option value={3}>3 Monate</option>
          <option value={6}>6 Monate</option>
          <option value={12}>12 Monate</option>
        </select>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
        <Stat title="Vorschläge" value={totals.count} hint={`${totals.shown} sichtbar`} />
        <Stat title="Ausgewählt" value={totals.selected} hint={euro(totals.selectedVolume)} />
        <Stat title="Bereits markiert" value={totals.flagged} hint="durch dich bestätigt" />
        <Stat title="Prüfvolumen" value={euro(totals.yearly)} hint="hochgerechnete Kandidaten" />
      </div>

      <section style={{ ...card, marginBottom: '12px', background: 'linear-gradient(135deg, #ffffff 0%, #ecf7f1 100%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: '#064734', fontSize: '17px' }}>Review-Queue</h2>
            <p style={{ ...muted, margin: '5px 0 0' }}>Erst Kandidaten markieren, dann rechts gesammelt als Geldfresser, überflüssig oder kündigungsreif speichern.</p>
          </div>
          <Pill tone={data?.aiConfigured ? 'green' : 'amber'}>{data?.aiConfigured ? 'KI aktiv' : 'Heuristik'}</Pill>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '180px 180px auto auto', gap: '8px', alignItems: 'center', marginTop: '12px' }}>
          <select value={priority} onChange={e => setPriority(e.target.value)} style={input}>
            <option value="all">Alle Vorschläge</option>
            <option value="high">Hohe Priorität</option>
            <option value="open">Noch offen</option>
            <option value="marked">Bereits markiert</option>
          </select>
          <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} style={input}>
            <option value="geldfresser">als Geldfresser</option>
            <option value="ueberfluessig">als Überflüssig</option>
            <option value="kuendigen">als Kündigung</option>
          </select>
          <button onClick={selectTop} style={button('#0f766e')}>Top-Kandidaten auswählen</button>
          <button onClick={saveSelected} disabled={bulkBusy || !selectedItems.length} style={button(selectedItems.length ? '#b42318' : '#94a3b8')}>{bulkBusy ? 'Speichere...' : 'Auswahl speichern'}</button>
        </div>
      </section>

      <section style={{ ...card, marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h2 style={{ margin: 0, color: '#064734', fontSize: '17px' }}>Letzte Bewertungen</h2>
            <p style={{ ...muted, margin: '5px 0 0' }}>Einzel- und Sammelbewertungen lassen sich hier zurücknehmen.</p>
          </div>
          <button onClick={load} style={button('#334155')}>Aktualisieren</button>
        </div>
        {!applications.length && <div style={muted}>Noch keine rücknehmbaren Bewertungen vorhanden.</div>}
        {applications.slice(0, 6).map(appRow => (
          <div key={appRow.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', alignItems: 'center', borderTop: '1px solid #eef4f1', paddingTop: '8px', marginTop: '8px' }}>
            <div>
              <div style={{ color: '#12231d', fontWeight: 900 }}>{appRow.label}</div>
              <div style={muted}>{appRow.affected_count} Buchungen - {appRow.created_at} - {appRow.undone_at ? `zurückgesetzt ${appRow.undone_at}` : 'aktiv'}</div>
            </div>
            {!appRow.undone_at ? (
              <button disabled={busyKey === `undo-${appRow.id}`} onClick={() => undoApplication(appRow)} style={button('#b42318')}>Rückgängig</button>
            ) : (
              <Pill tone="green">erledigt</Pill>
            )}
          </div>
        ))}
      </section>

      <div style={{ display: 'grid', gap: '12px' }}>
        {filtered.map(item => {
          const key = keyOf(item);
          return (
            <article key={key} style={{ ...card, borderColor: selected[key] ? '#0a6f4f' : '#d8e4dd' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '12px', alignItems: 'flex-start' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#064734', fontWeight: 900 }}>
                  <input type="checkbox" checked={!!selected[key]} onChange={e => toggleSelected(item, e.target.checked)} />
                  Auswahl
                </label>
                <div>
                  <h2 style={{ margin: 0, color: '#12231d', fontSize: '18px' }}>{item.empfaenger}</h2>
                  <p style={{ ...muted, margin: '4px 0 0' }}>{item.kategorie} - {item.anzahl} Buchungen - {item.monate} Monate - Score {item.score}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#0a6f4f', fontSize: '20px', fontWeight: 950 }}>{euro(item.gesamt)}</div>
                  <div style={muted}>Durchschnitt {euro(item.durchschnitt)}</div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '10px 0' }}>
                <Pill tone={item.score >= 70 ? 'red' : item.score >= 45 ? 'amber' : 'slate'}>{item.empfehlung}</Pill>
                {item.reasons.map(reason => <Pill key={reason} tone="blue">{reason}</Pill>)}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
                <Check label="Geldfresser" checked={!!item.ist_geldfresser} disabled={busyKey === key} onChange={v => save(item, { ist_geldfresser: v })} />
                <Check label="Notwendig" checked={!!item.ist_notwendig} disabled={busyKey === key} onChange={v => save(item, { ist_notwendig: v })} />
                <Check label="Überflüssig" checked={!!item.ist_ueberfluessig} disabled={busyKey === key} onChange={v => save(item, { ist_ueberfluessig: v })} />
                <Check label="Kündigen" checked={!!item.muss_kuendigen} disabled={busyKey === key} onChange={v => save(item, { muss_kuendigen: v })} />
              </div>

              <button onClick={() => nav(`/empfaenger?empfaenger=${encodeURIComponent(item.empfaenger)}&kategorie=${encodeURIComponent(item.kategorie)}`)} style={{ border: '1px solid #d8e4dd', background: '#fff', color: '#064734', borderRadius: '8px', padding: '8px 10px', fontWeight: 900, cursor: 'pointer', marginBottom: '8px' }}>Details prüfen</button>

              <details>
                <summary style={{ cursor: 'pointer', color: '#064734', fontWeight: 900 }}>Beispielbuchungen ansehen</summary>
                <div style={{ marginTop: '8px', display: 'grid', gap: '6px' }}>
                  {item.beispiele.map(b => (
                    <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', borderTop: '1px solid #eef4f1', paddingTop: '7px' }}>
                      <div>
                        <div style={{ color: '#12231d', fontWeight: 800 }}>{b.datum}</div>
                        <div style={muted}>{b.verwendungszweck || item.empfaenger}</div>
                      </div>
                      <strong style={{ color: '#b42318' }}>{euro(Math.abs(b.betrag))}</strong>
                    </div>
                  ))}
                </div>
              </details>
            </article>
          );
        })}
        {!filtered.length && <div style={card}>Keine Vorschläge für diesen Filter.</div>}
      </div>
    </div>
  );
}

function Check({ label, checked, disabled, onChange }) {
  return (
    <label style={{ border: '1px solid #d8e4dd', borderRadius: '8px', padding: '9px 10px', display: 'flex', gap: '8px', alignItems: 'center', background: checked ? '#dff3e9' : '#fff', color: '#12231d', fontWeight: 900 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Stat({ title, value, hint }) {
  return <div style={card}><div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div><div style={{ color: '#064734', fontSize: '24px', fontWeight: 950, marginTop: '6px' }}>{value}</div><div style={{ ...muted, marginTop: '3px' }}>{hint}</div></div>;
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  return <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

const input = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px 11px', background: 'white', color: '#12231d', fontWeight: 800 };
function button(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', fontWeight: 900 };
}
